/**
 * The field sweep on screen: the press that runs one, and the record of the
 * newest one keyed to a node.
 *
 * Every item is shown as what it is — one page's account of something
 * unsettled, quoted, with the page's host — and never as a finding about the
 * field. Items never become nodes; the one thing a person can do with an item
 * is ask a conjecture pass to explain it, which is a paid pass like any other
 * and lands conjectures, not the item.
 */

import { useId, type CSSProperties } from 'react'
import { useStore } from '../core/store'
import type { FieldSweep, ReasonNode, Session } from '../core/types'
import { SWEEP_KIND_LABEL, SWEEP_MAX_SEARCHES, latestSweep, sweepSentence } from '../core/sweep'
import { hostOf } from '../core/url'
import { calendarDay } from '../core/dates'
import { CONJECTURE_LINE_WORD, EXPLAIN_LOCK_REFUSAL, upNextShut } from '../core/upNext'
import { STATUS_WORD } from '../core/vocabulary'
import { runConjecture } from '../engine/reason'
import { Icon } from './components/Icon'
import { VerbPrice } from './VerbPrice'
import { NO_DEMO, pressFace, useLineRoute } from './ConjectureBlock'
import { pressName, pressTail } from './pressName'
import { pressVerb } from './upNextPress'
import './Conjectures.css'

/** What the sweep press buys, and what an item is not. */
export const SWEEP_PRESS_NOTE = `Up to ${SWEEP_MAX_SEARCHES} searches for where the literature on this is unsettled, each item quoting one returned page — a report of pages, never a node.`

/** The press. Paid, on the check role, and it says before it is pressed what an
 *  item is and is not. It waits on the Up next line as the source check does
 *  (D286). */
export function SweepPress({ node }: { node: ReasonNode }) {
  const route = useLineRoute('sweep')
  const shut = useStore((s) => upNextShut(s, 'sweep'))
  const noteId = useId()
  // A route with no search still presses, and its pass says why it refuses; the
  // note says so first, and nothing prices a search that cannot happen.
  const noSearch = route.ready && route.entry.search === 'none'
  const word = CONJECTURE_LINE_WORD.sweep
  const note = !route.ready
    ? `${route.keyLine} ${NO_DEMO}`
    : noSearch
      ? `${route.entry.name} has no web search, so there is no literature for a sweep to read.`
      : SWEEP_PRESS_NOTE
  return (
    <button
      type="button"
      className="action action--wide action--priced"
      style={{ '--act-tint': 'var(--sem-gap)' } as CSSProperties}
      // Greyed only where the line cannot take it, and still in the tab order:
      // its press path says why (D225).
      aria-disabled={shut || undefined}
      title={route.ready ? note : route.keyLine}
      aria-label={pressName(
        word,
        noSearch ? null : pressTail({ ready: route.ready, est: route.est }),
      )}
      aria-describedby={noteId}
      onClick={() => pressVerb('sweep', node.id)}
    >
      <b style={{ width: '100%' }}>
        <Icon name="search" size={13} />
        {pressFace(word, route.ready && !noSearch, route.est)}
        <VerbPrice
          verb="sweep"
          instead={!route.ready ? STATUS_WORD.needsKey : noSearch ? null : undefined}
        />
      </b>
      <span id={noteId}>{note}</span>
    </button>
  )
}

/** The item's words for a name, clipped: a statement can run to a paragraph. */
const clipped = (text: string, max = 60): string =>
  text.length > max ? `${text.slice(0, max - 1).trimEnd()}…` : text

/** The visible words of an item's press, short enough for a narrow column. */
export const EXPLAIN_WORD = 'Propose what explains this'

/**
 * "Propose what explains this", under one item: a conjecture pass on the swept
 * node, aimed at that item. Paid, on the reasoning route, priced as a move — and
 * the figure is printed at rest, where a `.btn`'s hover swap never showed it.
 *
 * Every item's button reads the same four words, so its name carries the item —
 * its statement, clipped, and its page's host — and then what it spends (D165).
 * It carries the item as a term, so it does not wait on the Up next line: under
 * a pass in flight it keeps its focus and its pass says so by name (D251, D286).
 * Keyless, it still presses, and its pass opens the reasoning route's key field.
 */
export function ExplainPress({
  nodeId,
  item,
}: {
  nodeId: string
  item: FieldSweep['items'][number]
}) {
  const route = useLineRoute('conjecture')
  const busy = useStore((s) => s.busy)
  return (
    <button
      type="button"
      className="btn btn--outline sv-explain"
      aria-disabled={busy || undefined}
      title={
        busy
          ? EXPLAIN_LOCK_REFUSAL
          : route.ready
            ? 'An insight pass aimed at this item: what would make it the expected case.'
            : route.keyLine
      }
      aria-label={pressName(
        `${EXPLAIN_WORD}: “${clipped(item.statement)}” on ${hostOf(item.url)}`,
        pressTail({ ready: route.ready, est: route.est }),
      )}
      onClick={() => void runConjecture(nodeId, { focus: item.id })}
    >
      <Icon name="conjecture" size={12} />
      {pressFace(EXPLAIN_WORD, route.ready, route.est)}
      {/* The figure is in the words above; keyless, the cell says so instead. */}
      <VerbPrice verb="move" instead={route.ready ? null : STATUS_WORD.needsKey} />
    </button>
  )
}

/** The newest sweep keyed to this node, or nothing. */
export function FieldSweepBlock({ session, node }: { session: Session; node: ReasonNode }) {
  const sweep = latestSweep(session, node.id)
  if (!sweep) return null
  const earlier = (session.sweeps ?? []).filter((x) => x.nodeId === node.id).length - 1
  const rewritten = sweep.subjectAtSweep !== `${node.label} — ${node.claim}`
  const q = sweep.queries.length
  const deletions = [
    sweep.dropped ? `${sweep.dropped} naming a page the search never returned` : null,
    sweep.unquoted ? `${sweep.unquoted} whose quote is not on its page` : null,
    sweep.malformed ? `${sweep.malformed} incomplete or repeating a page` : null,
    sweep.overCeiling ? `${sweep.overCeiling} past the ceiling` : null,
  ].filter(Boolean)
  return (
    <div className="group sv-sweep" style={{ gap: 'var(--s1)' }}>
      <span className="eyebrow">
        Field sweep · {calendarDay(sweep.searchedAt)} · {sweep.model.name}
      </span>
      <p className="gnote">{sweepSentence(sweep)}</p>
      {rewritten && (
        <p className="gnote gnote--warn">
          This sweep was of the subject as it read before a rewrite.
        </p>
      )}
      {sweep.items.length > 0 && (
        <ul className="cj-art">
          {sweep.items.map((item) => (
            <li key={item.id}>
              <span className="cj-art-meta">
                {SWEEP_KIND_LABEL[item.kind]}
                {item.year !== undefined ? ` · ${item.year}` : ''} · {hostOf(item.url)}
              </span>
              <span className="cj-art-covers">{item.statement}</span>
              <q className="cj-art-quote">{item.quote}</q>
              <a href={item.url} target="_blank" rel="noreferrer">
                {item.title}
              </a>
              <span className="gnote gnote--quiet">
                {item.quoteFound
                  ? 'The quoted sentence is on the page.'
                  : 'The quote could not be checked against the page: no page text came back.'}
              </span>
              <ExplainPress nodeId={sweep.nodeId} item={item} />
            </li>
          ))}
        </ul>
      )}
      <p className="gnote gnote--quiet">
        {q} quer{q === 1 ? 'y' : 'ies'} · {sweep.seen} distinct page{sweep.seen === 1 ? '' : 's'}{' '}
        returned
        {deletions.length ? ` · deleted: ${deletions.join(', ')}` : ''}.
      </p>
      {sweep.failures && sweep.failures.length > 0 && (
        <ul className="bullets">
          {sweep.failures.map((f) => (
            <li key={f}>{f}</li>
          ))}
        </ul>
      )}
      <p className="gnote gnote--quiet">
        Each item is what one page says, never a node on its own. An insight pass on this node, or
        anything below it, reads them as quoted data with their hosts.
        {earlier > 0
          ? ` ${earlier} earlier sweep${earlier === 1 ? ' is' : 's are'} kept in the record.`
          : ''}
      </p>
    </div>
  )
}
